import useHistoryCoursesApi from 'Hooks/useHistoryCoursesApi';
import { Box } from '@mui/system';
import { colors } from 'Constants/Colors';
import { DataGrid, type GridColDef, type GridSelectionModel } from '@mui/x-data-grid';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { TextField, InputAdornment, IconButton, Typography } from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import useDebounce from 'Hooks/useDebounce';

export interface CoursesTableHandle {
  getSelectedIds: () => string[];
}

interface CoursesTableProps {
  onSelectionChange?: (count: number) => void;
}

const CoursesTable = forwardRef<CoursesTableHandle, CoursesTableProps>(({ onSelectionChange }, ref) => {
  const { data, meta, loading, fetchHistoryCourses } = useHistoryCoursesApi({ init: false });
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const selectedIdsRef = useRef<Set<string | number>>(new Set());
  const [selectedCount, setSelectedCount] = useState(0);

  const debouncedSearch = useDebounce(searchText, 400);

  useImperativeHandle(ref, () => ({
    getSelectedIds: () => Array.from(selectedIdsRef.current).map(String),
  }));

  useEffect(() => {
    fetchHistoryCourses({
      page: page + 1,
      limit: pageSize,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  const handleClearSearch = () => {
    setSearchText('');
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };

  const rows = useMemo(
    () =>
      data.map(item => ({
        id: item.id,
        name: item.name,
      })),
    [data]
  );

  const visibleSelectionModel = useMemo(
    () => rows.filter(r => selectedIdsRef.current.has(r.id)).map(r => r.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedCount]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 150, hide: true },
      { field: 'name', headerName: 'Name', width: 300 },
    ],
    []
  );

  const handleSelectionChange = (newSelection: GridSelectionModel) => {
    rows.forEach(r => selectedIdsRef.current.delete(r.id));
    newSelection.forEach(id => selectedIdsRef.current.add(id));
    setSelectedCount(selectedIdsRef.current.size);
    onSelectionChange?.(selectedIdsRef.current.size);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        '& .MuiDataGrid-row.Mui-selected': {
          backgroundColor: `${colors.GREEN}1A`,
          '&:hover': {
            backgroundColor: `${colors.GREEN}33`,
          },
        },
        '& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate': {
          color: colors.GREEN,
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 1 }}>
        {selectedCount > 0 ? (
          <Typography variant="body2" sx={{ color: colors.GREEN, fontWeight: 500 }}>
            {selectedCount} fila{selectedCount !== 1 ? 's' : ''} seleccionada{selectedCount !== 1 ? 's' : ''}
          </Typography>
        ) : (
          <Box />
        )}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar..."
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          sx={{
            width: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              fontSize: '0.875rem',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.GREEN,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
      <DataGrid
        className="flex-1"
        rows={rows}
        columns={columns}
        loading={loading}
        localeText={{
          footerRowSelected: _ => '',
          noRowsLabel: 'No hay filas',
          noResultsOverlayLabel: 'No se encontraron resultados',
        }}
        checkboxSelection
        density="compact"
        disableColumnFilter
        disableColumnMenu
        paginationMode="server"
        rowCount={meta?.totalItems || 0}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[15, 25, 50]}
        selectionModel={visibleSelectionModel}
        onSelectionModelChange={handleSelectionChange}
        autoHeight
      />
    </Box>
  );
});

export default CoursesTable;
